import { useEffect, useRef, useState } from "react";
import style from "../style.module.scss";
import { Image, Spin, Tooltip } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { FaFileAlt } from "react-icons/fa";
import {
  GatePassStatusConstant,
  GatePassVehicleTypeConstant,
  RemovalSourceConstant,
} from "../core/_constant.gatePass";
import { StatusTag } from "../../../../common/components/Status/StatusTag";
import { API_URLs } from "../../../../utilities/API_URLs";
import { downloadPDF } from "../../../../utilities/downloadPdf";
import { getIdFromIri } from "../../../../common/utils/iri";

interface GatePassDetailsProps {
  value?: any;
}

export const GatePassDetails = ({ value }: GatePassDetailsProps) => {
  const {
    reference = "-",
    createdAt = "-",

    exitNote = null,

    checkedBy: {
      fullname = "-",
      email = "-",
    } = {},

    customer = null,
    uniqueIdentifierLoad = "-",

    removalSource: { id: removalSourceId = "-" } = {},

    quantityOut = "-",
    bagCount = null,
    invoiceNumber = "-",

    carrierName = "-",
    forwarderAgentName = "-",

    vehicleType: {
      id: vehicleTypeId = "-",
      name: vehicleType = "-",
    } = {},

    truck: {
      reference: truckReference = "-",
    } = {},

    particularTruckNumber = "-",
    particularDriverName = "-",
    driverLicenseNumber = "-",
    driverIdCardNumber = "-",

    damages = [],
    comment = "-",
  } = value || {};

  const images = value?.images ?? [];

  const exitNoteId = getIdFromIri(exitNote);
  const [isDownloadingExitNote, setIsDownloadingExitNote] = useState(false);

  const imagesContainerRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(images.length);
  const [showAllImages, setShowAllImages] = useState(false);

  useEffect(() => {
    const container = imagesContainerRef.current;

    if (!container || !images.length) {
      return;
    }

    const calculateVisibleImages = () => {
      const containerWidth = container.clientWidth;

      // largeur d'une image + gap
      const imageWidth = 140;
      const gap = 12;

      const count = Math.floor(
        (containerWidth + gap) / (imageWidth + gap)
      );

      setVisibleCount(Math.max(1, count));
    };

    calculateVisibleImages();

    const resizeObserver = new ResizeObserver(calculateVisibleImages);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [images.length]);

  const handleDownloadExitNote = async () => {
    if (!exitNoteId) return;

    try {
      setIsDownloadingExitNote(true);
      await downloadPDF(
        API_URLs.exitNote.exitNotePdfUrl.replace("{id}", exitNoteId)
      );
    } catch (error) {
      console.log(error);
    } finally {
      setIsDownloadingExitNote(false);
    }
  };

  return (
    <div className={style.details}>
      {/* Informations sur l'enlèvement */}
      <section className={style.details__section}>
        <h3 className={style.details__title}>
          Informations sur l'enlèvement
        </h3>

        <div className={style.details__grid}>
          <div className={style.details__item}>
            <span>Référence</span>
            <strong>{reference}</strong>
          </div>

          <div className={style.details__item}>
            <span>Statut</span>
            <StatusTag
              value={GatePassStatusConstant.getStatusKey(value)}
              options={GatePassStatusConstant.OPTIONS}
            />
          </div>

          <div className={style.details__item}>
            <span>Date de l'enlèvement</span>
            <strong>{createdAt}</strong>
          </div>

          <div className={style.details__item}>
            <span>Contrôlé par</span>
            <strong>{fullname}</strong>

            {email !== "-" && (
              <small className={style.details__item__secondary}>
                {email}
              </small>
            )}
          </div>

          <div className={style.details__item}>
            <span>Source de l'enlèvement</span>
            <StatusTag
              value={removalSourceId}
              options={RemovalSourceConstant.OPTIONS}
            />
          </div>
        </div>
      </section>

      {/* Bon de sortie */}
      <section className={style.details__section}>
        <h3 className={style.details__title}>Bon de sortie</h3>

        {exitNoteId ? (
          <div className={style.details__document}>
            <div className={style.details__document__info}>
              <span className={style.details__document__name}>
                Bon de sortie N° {exitNoteId}
              </span>
              <span className={style.details__document__meta}>
                Cet enlèvement est rattaché à un bon de sortie généré.
              </span>
            </div>

            {isDownloadingExitNote ? (
              <Spin indicator={<LoadingOutlined spin />} size="small" />
            ) : (
              <Tooltip title={"Télécharger le bon de sortie"}>
                <div
                  className={`${style.details__document__link} cursor-pointer d-flex align-items-center gap-2`}
                  onClick={handleDownloadExitNote}
                >
                  <FaFileAlt />
                  <span>PDF</span>
                </div>
              </Tooltip>
            )}
          </div>
        ) : (
          <span className={style.details__empty}>
            Aucun bon de sortie rattaché à cet enlèvement.
          </span>
        )}
      </section>

      {/* Client & chargement */}
      <section className={style.details__section}>
        <h3 className={style.details__title}>Client & chargement</h3>

        <div className={style.details__grid}>
          <div className={style.details__item}>
            <span>Client / Transitaire</span>
            <strong>
              {customer && typeof customer === "object"
                ? customer?.name ?? "-"
                : "-"}
            </strong>
          </div>

          <div className={style.details__item}>
            <span>Identifiant chargement</span>
            <strong>{uniqueIdentifierLoad}</strong>
          </div>
        </div>
      </section>

      {/* Quantités & facturation */}
      <section className={style.details__section}>
        <h3 className={style.details__title}>
          Quantités & facturation
        </h3>

        <div className={style.details__grid}>
          <div className={style.details__item}>
            <span>Tonnage(T)</span>
            <strong>{quantityOut}</strong>
          </div>

          {bagCount !== null && (
            <div className={style.details__item}>
              <span>Nombre de sorties</span>
              <strong>{bagCount}</strong>
            </div>
          )}

          <div className={style.details__item}>
            <span>N° Facture</span>
            <strong>{invoiceNumber}</strong>
          </div>
        </div>
      </section>

      {/* Transport */}
      <section className={style.details__section}>
        <h3 className={style.details__title}>Transport</h3>

        <div className={style.details__grid}>
          <div className={style.details__item}>
            <span>Type de véhicule</span>
            <strong>{vehicleType}</strong>
          </div>

          {vehicleTypeId === GatePassVehicleTypeConstant.INTERNAL_ID && (
            <div className={style.details__item}>
              <span>Camion</span>
              <strong>{truckReference}</strong>
            </div>
          )}

          {vehicleTypeId === GatePassVehicleTypeConstant.PARTICULAR_ID && (
            <>
              <div className={style.details__item}>
                <span>N° véhicule</span>
                <strong>{particularTruckNumber}</strong>
              </div>

              <div className={style.details__item}>
                <span>Conducteur</span>
                <strong>{particularDriverName}</strong>
              </div>

              <div className={style.details__item}>
                <span>N° permis de conduire</span>
                <strong>{driverLicenseNumber}</strong>
              </div>

              <div className={style.details__item}>
                <span>N° pièce d'identité</span>
                <strong>{driverIdCardNumber}</strong>
              </div>
            </>
          )}

          <div className={style.details__item}>
            <span>Transporteur</span>
            <strong>{carrierName}</strong>
          </div>

          <div className={style.details__item}>
            <span>Agent transitaire</span>
            <strong>{forwarderAgentName}</strong>
          </div>
        </div>
      </section>

      {/* Avaries constatées */}
      <section className={style.details__section}>
        <h3 className={style.details__title}>Avaries constatées</h3>

        {damages.length > 0 ? (
          <div className={style.details__damages}>
            {damages.map((damage: any, index: number) => (
              <div
                key={damage.id ?? index}
                className={style.details__damage}
              >
                {damage.name ?? "-"}
              </div>
            ))}
          </div>
        ) : (
          <span className={style.details__empty}>
            Aucune avarie constatée.
          </span>
        )}
      </section>

      {/* Commentaires */}
      <section className={style.details__section}>
        <h3 className={style.details__title}>Commentaire</h3>

        {comment && comment !== "-" ? (
          <div className={style.details__comment}>{comment}</div>
        ) : (
          <span className={style.details__empty}>Aucun commentaire.</span>
        )}
      </section>

      {/* Images */}
      <section className={style.details__section}>
        <h3 className={style.details__title}>Images</h3>

        {images.length > 0 ? (
          <div
            ref={imagesContainerRef}
            className={`${style.details__images} ${
              showAllImages ? style.details__images__expanded : ""
            }`}
          >
            {images.map((image: any, index: number) => {
              const hasMoreImages = images.length > visibleCount;

              const isLastVisibleImage =
                !showAllImages &&
                hasMoreImages &&
                index === visibleCount - 1;

              return (
                <div
                  key={image["@id"] || index}
                  className={style.details__image}
                >
                  <Image
                    src={image.contentUrl}
                    alt={image.reference || `Image ${index + 1}`}
                    preview={!isLastVisibleImage}
                    className={
                      isLastVisibleImage
                        ? style.details__image__blurred
                        : ""
                    }
                  />

                  {isLastVisibleImage && (
                    <div
                      className={style.details__image__overlay}
                      onClick={() => setShowAllImages(true)}
                    >
                      <span>Voir toutes les images</span>
                    </div>
                  )}
                </div>
              );
            })}
            {showAllImages && images.length > visibleCount && (
              <button
                type="button"
                className={style.details__imagesToggle}
                onClick={() => setShowAllImages(false)}
              >
                Réduire les images
              </button>
            )}
          </div>
        ) : (
          <div className={style.details__empty}>Aucune image.</div>
        )}
      </section>
    </div>
  );
};
